import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isRejectionStatus } from "../_helpers/load-status";
import { useFitExternalLoadStore } from "@/modules/fit/external/load/_hooks/use-load-store";

type RelocationOrder = components["schemas"]["RelocationOrder"];

const CANNOT_PICK_UP_REASONS: Array<{ value: RelocationOrderStatus; translationKey: string }> = [
  { value: RelocationOrderStatus.TransportCompanyRejectedDamagedVehicle, translationKey: "VehicleDamaged" },
  { value: RelocationOrderStatus.TransportCompanyRejectedMissingVehicle, translationKey: "VehicleMissing" },
  { value: RelocationOrderStatus.TransportCompanyRejectedMissingKeys, translationKey: "KeysMissing" },
] as const;

interface CannotPickUpReasonSelectorProps {
  relocationOrder: RelocationOrder;
}

export function CannotPickUpReasonSelector({ relocationOrder }: CannotPickUpReasonSelectorProps) {
  const { t } = useTranslation("fit");
  const currentStatus = useFitExternalLoadStore((s) => s.loadStatuses[relocationOrder.uuid]);
  const setCannotPickUpReason = useFitExternalLoadStore((s) => s.setCannotPickUpReason);
  const hasRejectionStatus = currentStatus && isRejectionStatus(currentStatus);
  const currentReason = hasRejectionStatus ? currentStatus : "";

  return (
    <div className="border-error border-l-4 p-6">
      <div className="mb-4 flex items-center gap-3">
        <XCircle className="text-error h-6 w-6" />
        <span className="text-error text-lg font-medium">{t("fit:ReasonForRejection")}</span>
      </div>
      <Select
        value={currentReason}
        onValueChange={(value: RelocationOrderStatus) => setCannotPickUpReason(relocationOrder, value)}
      >
        <SelectTrigger className="h-14 w-full text-lg">
          <SelectValue placeholder={t("common:SelectReason")} />
        </SelectTrigger>
        <SelectContent>
          {CANNOT_PICK_UP_REASONS.map((reason) => (
            <SelectItem key={reason.value} value={reason.value} className="py-3 text-lg">
              {t(`fit:${reason.translationKey}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
